import Header from '../components/Header';
import Footer from '../components/Footer';
import { Heart, Shield, AlertCircle, ExternalLink } from 'lucide-react';

const ResponsibleGaming = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <article className="glass-strong rounded-3xl p-8 md:p-12">
            <div className="flex items-center space-x-3 mb-8">
              <Heart className="w-8 h-8 text-green-600" />
              <h1 className="pb-2 text-4xl md:text-5xl font-bold bg-gradient-to-r from-pink-600 to-green-600 bg-clip-text text-transparent">
                Responsible Gaming
              </h1>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
              <p className="text-xl text-gray-800 font-medium">
                At Crown Jackpot, we are committed to promoting responsible gambling and protecting players from gambling-related harm. We believe that gambling should be enjoyable entertainment, not a source of financial or personal problems. This page provides information about responsible gambling, tools to help you stay in control, and resources if you need support.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">What is Responsible Gambling?</h2>

              <p>
                Responsible gambling means maintaining control over your gambling activities, ensuring that gambling remains a form of entertainment rather than a necessity or compulsion. It involves setting limits on time and money spent gambling, understanding the risks involved, recognizing when gambling is no longer enjoyable, and seeking help when needed.
              </p>

              <p>
                Gambling should always be viewed as entertainment, not as a way to make money or solve financial problems. The house always has an edge, meaning that over time, the casino will win more than players lose. This is a fundamental aspect of how gambling works, not a flaw that can be overcome through skill or strategy.
              </p>

              <div className="glass p-6 rounded-2xl my-8 border-l-4 border-green-500">
                <div className="flex items-start space-x-3">
                  <Shield className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Core Principles</h3>
                    <ul className="text-gray-700 space-y-2">
                      <li>• Only gamble with money you can afford to lose</li>
                      <li>• Never chase losses by betting more</li>
                      <li>• Set time and money limits before you start</li>
                      <li>• Never gamble when emotional, intoxicated, or under stress</li>
                      <li>• View gambling as entertainment, not an income source</li>
                      <li>• Take regular breaks from gambling</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Warning Signs of Problem Gambling</h2>

              <p>
                Problem gambling can develop gradually, and recognizing warning signs early is crucial. If you recognize any of these signs in yourself or someone you know, it may be time to seek help:
              </p>

              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Spending more time or money gambling than you intended</li>
                <li>Chasing losses by betting more to recover losses</li>
                <li>Lying to family or friends about gambling activities</li>
                <li>Gambling interfering with work, relationships, or responsibilities</li>
                <li>Borrowing money or selling possessions to gamble</li>
                <li>Feeling restless or irritable when trying to stop gambling</li>
                <li>Gambling to escape problems or relieve feelings of depression or anxiety</li>
                <li>Needing to gamble with increasing amounts to achieve excitement</li>
                <li>Experiencing relationship, work, or financial problems due to gambling</li>
                <li>Feeling guilty or anxious about gambling but unable to stop</li>
              </ul>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Tools for Responsible Gambling</h2>

              <p>
                The UK online casinos we review provide various tools to help you gamble responsibly. These tools include:
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-6 mb-3">Deposit Limits</h3>
              <p>
                Set daily, weekly, or monthly limits on how much you can deposit into your casino account. These limits help you control your spending and prevent excessive gambling.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-6 mb-3">Loss Limits</h3>
              <p>
                Set limits on how much you can lose over a specific period. Once you reach your loss limit, you cannot continue gambling until the limit resets.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-6 mb-3">Session Time Limits</h3>
              <p>
                Set limits on how long you can play in a single session. When your session time expires, you'll be automatically logged out.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-6 mb-3">Reality Checks</h3>
              <p>
                Receive periodic reminders about how long you've been playing and how much you've spent during your current session.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-6 mb-3">Self-Exclusion</h3>
              <p>
                Temporarily or permanently exclude yourself from gambling sites. Self-exclusion prevents you from accessing your account for a specified period.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Gamstop - National Self-Exclusion Scheme</h2>

              <p>
                Gamstop is a free service that allows UK players to self-exclude from all UK-licensed gambling sites simultaneously. Once you register with Gamstop, you will be excluded from all participating operators for a minimum period of your choice (6 months, 1 year, or 5 years). Self-exclusions cannot be reversed during the exclusion period.
              </p>

              <p>
                To register with Gamstop, visit <a href="https://www.gamstop.co.uk" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">gamstop.co.uk</a>. The service is free, confidential, and can be accessed 24/7.
              </p>

              <div className="glass p-6 rounded-2xl my-8 border-l-4 border-pink-500">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-pink-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Important Information</h3>
                    <p className="text-gray-700">
                      If you're struggling with problem gambling, you're not alone. Help is available, and recovery is possible. There's no shame in seeking support, and professional help can make a significant difference. Don't hesitate to reach out to support organizations if you need assistance.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Support Resources</h2>

              <p>
                If you're experiencing problems with gambling, numerous organizations provide free, confidential support:
              </p>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="glass p-6 rounded-2xl">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">BeGambleAware</h3>
                  <p className="text-gray-700 mb-3">
                    Provides information, advice, and free support for anyone affected by problem gambling.
                  </p>
                  <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1 text-green-600 hover:underline">
                    <span>Visit BeGambleAware</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>

                <div className="glass p-6 rounded-2xl">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">GamCare</h3>
                  <p className="text-gray-700 mb-3">
                    Offers free support, advice, and counseling for anyone affected by problem gambling.
                  </p>
                  <a href="https://www.gamcare.org.uk" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1 text-green-600 hover:underline">
                    <span>Visit GamCare</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>

                <div className="glass p-6 rounded-2xl">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Gamblers Anonymous</h3>
                  <p className="text-gray-700 mb-3">
                    A fellowship of men and women who share their experience, strength, and hope to solve their gambling problem.
                  </p>
                  <a href="https://www.gamblersanonymous.org.uk" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1 text-green-600 hover:underline">
                    <span>Visit GA</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>

                <div className="glass p-6 rounded-2xl">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">National Gambling Helpline</h3>
                  <p className="text-gray-700 mb-3">
                    24/7 confidential support and advice for anyone affected by problem gambling.
                  </p>
                  <p className="text-green-600 font-semibold">0808 8020 133</p>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Age Restrictions</h2>

              <p>
                Online gambling is restricted to individuals aged 18 and over in the UK. It is illegal for anyone under 18 to gamble online. All UK-licensed casinos are required to verify the age of their customers before allowing them to gamble.
              </p>

              <p>
                If you are a parent or guardian, we encourage you to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Monitor your children's internet usage</li>
                <li>Use parental controls to block gambling websites</li>
                <li>Educate your children about the risks of gambling</li>
                <li>Secure your payment methods to prevent unauthorized use</li>
                <li>Report any suspected underage gambling immediately</li>
              </ul>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Our Commitment</h2>

              <p>
                At Crown Jackpot, we are committed to promoting responsible gambling and protecting players. We only recommend casinos that are licensed by the UK Gambling Commission and that implement comprehensive responsible gambling measures. We provide information about responsible gambling tools and support resources to help players make informed decisions.
              </p>

              <p>
                We encourage all players to gamble responsibly, set limits, recognize warning signs, and seek help when needed. Remember, gambling should be fun and entertaining, not a source of stress or financial difficulty. If gambling is no longer enjoyable, it's time to stop and seek support.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Contact Us</h2>

              <p>
                If you have questions about responsible gambling or our commitment to player protection, please contact us at:
              </p>

              <div className="glass p-6 rounded-2xl my-6">
                <p className="font-semibold text-gray-800 mb-2">Crown Jackpot</p>
                <p className="text-gray-700">Email: info@crown-jackpot.com</p>
                <p className="text-gray-700">Phone: +44 9296 526 586</p>
                <p className="text-gray-700">Address: London, United Kingdom</p>
              </div>
            </div>
          </article>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResponsibleGaming;

